import type { WebPushSubscribeBody } from 'src/openapi';
import {
  LS_KEY_WEB_PUSH_ENDPOINT,
  LS_KEY_WEB_PUSH_OPTED_OUT,
  webPushOptedOutKey,
} from 'src/constants/localStorageKeys';
import apiClient from './apiClient';
import { getHeaderDeviceType } from './utils';

const SERVICE_WORKER_URL = '/sw.js';

/**
 * Web Push is only offered in a real browser tab. Inside the app's WebView the
 * native app has its own push, and a browser permission prompt there would be
 * asking for something the app already owns. iOS Safari only exposes
 * PushManager to sites added to the home screen, so it reads as unsupported.
 */
export const isWebPushSupported = () =>
  typeof window !== 'undefined' &&
  !(window as any).ReactNativeWebView &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export const getPushPermission = (): NotificationPermission | 'unsupported' =>
  isWebPushSupported() ? Notification.permission : 'unsupported';

const urlBase64ToUint8Array = (base64: string) => {
  const padded = `${base64}${'='.repeat((4 - (base64.length % 4)) % 4)}`
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = window.atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const sameKey = (subscription: PushSubscription, key: Uint8Array) => {
  const current = subscription.options.applicationServerKey;
  if (!current) return false;
  const bytes = new Uint8Array(current);
  return bytes.length === key.length && bytes.every((b, i) => b === key[i]);
};

export const getExistingPushSubscription = async () => {
  if (!isWebPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration('/');
  return registration ? registration.pushManager.getSubscription() : null;
};

/**
 * The browser's subscription for this site, created if needed. A subscription
 * made with a different VAPID key (the server's pair was rotated) can never be
 * sent to, so it is replaced rather than reused.
 */
export const ensurePushSubscription = async (vapidPublicKey: string) => {
  await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: '/' });
  const registration = await navigator.serviceWorker.ready;
  const key = urlBase64ToUint8Array(vapidPublicKey);
  const existing = await registration.pushManager.getSubscription();
  if (existing && sameKey(existing, key)) return existing;
  if (existing) await existing.unsubscribe();
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: key,
  });
};

// Browser storage can be missing or throw (private windows, blocked site data);
// losing this only means a stale server row waits for its first failed send.
export const rememberPushEndpoint = (endpoint: string) => {
  try {
    localStorage.setItem(LS_KEY_WEB_PUSH_ENDPOINT, endpoint);
  } catch {
    // see above
  }
};

export const takeRememberedPushEndpoint = () => {
  try {
    const endpoint = localStorage.getItem(LS_KEY_WEB_PUSH_ENDPOINT);
    localStorage.removeItem(LS_KEY_WEB_PUSH_ENDPOINT);
    return endpoint;
  } catch {
    return null;
  }
};

/**
 * Per account, not per browser: on a shared machine one person switching
 * notifications off says nothing about the next. A flag from before it was
 * keyed is taken as the current account's -- usually the only one here.
 */
export const isPushOptedOut = (userId: string) => {
  try {
    const key = webPushOptedOutKey(userId);
    if (localStorage.getItem(LS_KEY_WEB_PUSH_OPTED_OUT) === 'true') {
      localStorage.setItem(key, 'true');
      localStorage.removeItem(LS_KEY_WEB_PUSH_OPTED_OUT);
    }
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

export const setPushOptedOut = (userId: string, optedOut: boolean) => {
  try {
    const key = webPushOptedOutKey(userId);
    if (optedOut) localStorage.setItem(key, 'true');
    else localStorage.removeItem(key);
  } catch {
    // Storage blocked: the sync may re-subscribe; the switch still works.
  }
};

/**
 * Chrome's prompt offers "Remember my decision: until I close this site", and
 * an allow given that way lapses silently when the tab closes — exactly when
 * a push is meant to arrive. There is no API to read which option was picked,
 * so the only lever is a word before the prompt opens -- and only on desktop
 * Chromium, the one prompt that offers the choice.
 */
export const isChromiumPrompt = () => {
  const uaData = (navigator as any).userAgentData;
  // Desktop only: Android Chrome's prompt is a plain Block / Allow with no
  // duration to pick, so the tip would describe an option that isn't there.
  return (
    !!uaData &&
    !uaData.mobile &&
    !!uaData.brands?.some(({ brand }: { brand: string }) => /Chrom/.test(brand))
  );
};

export const toSubscribeBody = (
  subscription: PushSubscription,
): WebPushSubscribeBody => {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint ?? subscription.endpoint,
    keys: {
      p256dh: json.keys?.p256dh ?? '',
      auth: json.keys?.auth ?? '',
    },
    device_type: getHeaderDeviceType(),
  };
};

/**
 * Push cleanup on the way out is best effort and bounded: signing out must
 * never fail, or hang, because it did.
 */
const SIGN_OUT_CLEANUP_TIMEOUT_MS = 2000;

const bounded = (cleanup: Promise<void>) =>
  Promise.race([
    cleanup,
    new Promise<void>((resolve) => {
      setTimeout(resolve, SIGN_OUT_CLEANUP_TIMEOUT_MS);
    }),
  ]);

/**
 * The browser is unsubscribed first: it is what actually stops pushes arriving
 * on a shared machine, so it must not wait on a server call that can fail or
 * outlast the timeout. The server is then told the endpoint, which it would
 * otherwise only drop on its next failed send.
 */
const removeSubscription = async () => {
  try {
    const subscription = await getExistingPushSubscription();
    takeRememberedPushEndpoint();
    if (!subscription) return;
    const { endpoint } = subscription;
    try {
      await subscription.unsubscribe();
    } finally {
      await (
        await apiClient.getNotificationsApi()
      ).notificationApiDeleteWebPushSubscription({
        webPushUnsubscribeBody: { endpoint },
      });
    }
  } catch {
    // The server also drops endpoints that stop accepting pushes.
  }
};

export const removeWebPushSubscriptionOnLogout = () =>
  bounded(removeSubscription());

/**
 * Signed out without the logout button (token expired, 401, account deleted):
 * the server can't be told any more, so the browser's own subscription is
 * dropped. The endpoint then answers 410 and the server deletes the row on
 * the next send, so no pushes reach a browser nobody is signed in on.
 */
export const dropSignedOutPushSubscription = () =>
  bounded(
    (async () => {
      try {
        const subscription = await getExistingPushSubscription();
        takeRememberedPushEndpoint();
        if (subscription) await subscription.unsubscribe();
      } catch {
        // Nothing else to do: the server's own failure cleanup still applies.
      }
    })(),
  );
